import { useTranslation } from 'react-i18next';
import { Box, DatepickerSimple, ErrorText, Radio, TextField, Timestamp, withStyles } from 'vopay';
import styles from './RecurrenceEndsField.module.scss';

const cn = withStyles(styles);

export type RecurrenceEndsValue = { mode: 'date'; date: string } | { mode: 'occurrences'; count: string };

export type RecurrenceEndsFieldProps = {
  ends: RecurrenceEndsValue;
  onEndsChange: (ends: RecurrenceEndsValue) => void;
  minEndDate?: Timestamp;
  disabled?: boolean;
  dateError?: string | null;
  occurrencesError?: string | null;
};

// "Ends: On {date} / After {N} occurrences" — shared by every non-one-time Recurrence preset
// (extracted out of what used to be Custom-only UI, since every preset's Recurring Payment
// card now shows this section.
export default function RecurrenceEndsField({
  ends,
  onEndsChange,
  minEndDate,
  disabled,
  dateError,
  occurrencesError,
}: RecurrenceEndsFieldProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'UI.RecurrenceEndsField' });
  const activeError = ends.mode === 'date' ? dateError : occurrencesError;

  return (
    <Box flx="col" ai="fs" gapSize="8px" className={cn('row')}>
      <span>
        {t('ends', { defaultValue: 'Ends' })} <span className={cn('requiredAsterisk')}>*</span>
      </span>
      <div className={cn('endsRadioGroup')}>
        <Radio
          name="recurrenceEnds"
          value={ends.mode}
          disabled={disabled}
          onChange={(e) => {
            const mode = e.target.value;
            onEndsChange(
              mode === 'date'
                ? { mode: 'date', date: ends.mode === 'date' ? ends.date : minEndDate ? String(minEndDate) : '' }
                : { mode: 'occurrences', count: ends.mode === 'occurrences' ? ends.count : '' },
            );
          }}
          options={{
            date: (
              <Box ai="center" gapSize="8px" width="100%">
                <span>{t('endsOn', { defaultValue: 'On' })}</span>
                <div className={cn('endsDateField')}>
                  <DatepickerSimple
                    variant="outlineShadow"
                    name="recurrenceEndDate"
                    inputWidth="100%"
                    isDisabled={disabled || ends.mode !== 'date'}
                    min={minEndDate}
                    value={ends.mode === 'date' ? ends.date : ''}
                    onChange={(e) => onEndsChange({ mode: 'date', date: e.target.value })}
                  />
                </div>
              </Box>
            ),
            occurrences: (
              <Box ai="center" gapSize="8px">
                <span>{t('endsAfter', { defaultValue: 'After' })}</span>
                <div className={cn('occurrencesInput')}>
                  <TextField
                    variant="outlineShadow"
                    // numericWholePositive leaves cleave's default thousands grouping on (e.g.
                    // "1,000"), which every consumer then fails to parse with a bare Number() —
                    // positiveInteger is the same digits-only mask without that grouping.
                    preset="positiveInteger"
                    name="endsAfterOccurrences"
                    disabled={disabled || ends.mode !== 'occurrences'}
                    value={ends.mode === 'occurrences' ? ends.count : ''}
                    onChange={(e) => onEndsChange({ mode: 'occurrences', count: e.target.value })}
                  />
                </div>
                <span>{t('endsOccurrences', { defaultValue: 'occurrences' })}</span>
              </Box>
            ),
          }}
        />
      </div>
      <ErrorText error={activeError} size="sm" fitContent />
    </Box>
  );
}
